/**
 * ChangePasswordSection — schimbare parolă master, zero-knowledge.
 *
 * Flow:
 *   1. User introduce parola curentă + parolă nouă + confirm
 *   2. Client:
 *      - deriv master_key_old din (parola_curentă, salt_curent) + compute auth_hash_old
 *      - decrypt private_key cu master_key_old (verify că parola curentă e corectă)
 *      - generate salt_new + deriv master_key_new (parolă nouă, salt nou, params default)
 *      - compute auth_hash_new
 *      - encrypt private_key cu master_key_new → private_key_encrypted_new
 *   3. POST /auth/change-password cu toate valorile
 *   4. La succes: rotateMasterKey în store (update sessionStorage)
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import libsodium from 'libsodium-wrappers-sumo';

import { authApi } from '@/api/auth';
import RecoveryPhraseCard from '@/components/RecoveryPhraseCard';
import { buildRecoveryBundle, generateRecoveryPhrase } from '@/crypto/recovery';
import { errorMessage } from '@/api/client';
import {
  base64ToBytes,
  bytesToBase64,
  computeAuthHash,
  decryptSym,
  DEFAULT_KDF_PARAMS,
  deriveMasterKey,
  encryptSym,
  ensureSodium,
  generateSalt,
  kdfParamsFromServer,
} from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import HibpCheck from '@/features/vault/HibpCheck';
import { PasswordStrength } from '@/features/vault/PasswordTools';

export default function ChangePasswordSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">{t('settings.master_password_section')}</div>
          <div className="text-xs text-[var(--text-dim)]">
            {t('settings.master_password_description')}
          </div>
        </div>
        {!open && (
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={() => setOpen(true)}
          >
            {t('settings.change_password')}
          </button>
        )}
      </div>
      {open && <ChangeForm onClose={() => setOpen(false)} />}
    </div>
  );
}

function ChangeForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const masterKey = useAuthStore((s) => s.masterKey);
  const keyPair = useAuthStore((s) => s.keyPair);
  const rotateMasterKey = useAuthStore((s) => s.rotateMasterKey);

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [newPhrase, setNewPhrase] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPwd.length < 8) {
      setError(t('security_section.new_password_placeholder'));
      return;
    }
    if (newPwd !== confirmPwd) {
      setError(t('security_section.passwords_not_match'));
      return;
    }
    if (newPwd === currentPwd) {
      setError(t('security_section.new_password_different'));
      return;
    }
    if (!user || !masterKey || !keyPair) {
      setError(t('security_section.incomplete_session'));
      return;
    }

    setBusy(true);
    try {
      await ensureSodium();

      // 1. Re-deriv master_key_old din parolă curentă + salt user curent
      const saltResp = await authApi.getOwnKdf();
      const oldSalt = base64ToBytes(saltResp.kdf_salt);
      const masterKeyOld = await deriveMasterKey(
        currentPwd,
        oldSalt,
        kdfParamsFromServer(saltResp.kdf_params),
      );

      // Verifică împotriva master_key din memorie (constant-time)
      if (!constantTimeEqual(masterKeyOld, masterKey)) {
        libsodium.memzero(masterKeyOld);
        setError(t('security_section.current_password_wrong'));
        return;
      }

      // 2. Generate new salt + derive new master_key (params default curent)
      const newSalt = await generateSalt();
      const newKdfParams = {
        mem_kib: DEFAULT_KDF_PARAMS.memlimit / 1024,
        iters: DEFAULT_KDF_PARAMS.opslimit,
      };
      const masterKeyNew = await deriveMasterKey(newPwd, newSalt, {
        opslimit: newKdfParams.iters,
        memlimit: newKdfParams.mem_kib * 1024,
      });

      // 3. Re-encrypt private_key cu masterKey_new
      const reEncrypted = await encryptSym(keyPair.privateKey, masterKeyNew);

      // Sanity verify — decrypt înapoi și compară. Previne coruperea în
      // cazul unui bug subtil în WebCrypto/sodium.
      const verify = await decryptSym(
        { ciphertext: reEncrypted.ciphertext, nonce: reEncrypted.nonce },
        masterKeyNew,
      );
      if (!constantTimeEqual(verify, keyPair.privateKey)) {
        libsodium.memzero(masterKeyOld);
        libsodium.memzero(masterKeyNew);
        libsodium.memzero(verify);
        throw new Error(t('security_section.re_encrypt_verification_failed'));
      }
      libsodium.memzero(verify);

      // 4. Compute auth_hashes
      const authHashOldBytes = await computeAuthHash(masterKeyOld);
      const authHashNewBytes = await computeAuthHash(masterKeyNew);

      // 5. Frază de recuperare nouă — cea veche moare odată cu parola veche.
      const nextPhrase = generateRecoveryPhrase();
      const recovery = await buildRecoveryBundle(nextPhrase, keyPair);

      await authApi.changePassword({
        recovery,
        auth_hash_old: bytesToBase64(authHashOldBytes),
        auth_hash_new: bytesToBase64(authHashNewBytes),
        kdf_salt: bytesToBase64(newSalt),
        kdf_params: {
          algorithm: 'argon2id',
          mem_kib: newKdfParams.mem_kib,
          iters: newKdfParams.iters,
          parallelism: 1,
          key_len: 32,
        },
        private_key_encrypted: bytesToBase64(reEncrypted.ciphertext),
        private_key_nonce: bytesToBase64(reEncrypted.nonce),
      });

      libsodium.memzero(authHashOldBytes);
      libsodium.memzero(authHashNewBytes);
      libsodium.memzero(masterKeyOld);

      // Rotate master key în store. keyPair rămâne același.
      rotateMasterKey(masterKeyNew);

      void queryClient.invalidateQueries({ queryKey: ['recovery-status'] });
      // Rămânem deschiși până userul confirmă că a salvat fraza nouă.
      setNewPhrase(nextPhrase);
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      setError(errorMessage(err, t('security_section.change_password_error')));
    } finally {
      setBusy(false);
    }
  };

  if (newPhrase) {
    return (
      <div className="mt-3 space-y-3" data-testid="password-changed">
        <p className="text-xs text-green-600 dark:text-green-400">
          {t('security_section.changed_success')}
        </p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {t('recovery.rotated_hint')}
        </p>
        <RecoveryPhraseCard phrase={newPhrase} email={user?.email ?? ''} />
        <button
          type="button"
          className="btn-primary text-xs"
          data-testid="password-changed-close"
          onClick={onClose}
        >
          {t('recovery.saved_close')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-3">
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {t('security_section.change_password_hint')}
      </p>
      <input
        type="password"
        placeholder={t('security_section.current_password_placeholder')}
        autoComplete="current-password"
        className="input font-mono text-xs"
        value={currentPwd}
        onChange={(e) => setCurrentPwd(e.target.value)}
        disabled={busy}
        required
      />
      <div>
        <input
          type="password"
          placeholder={t('security_section.new_password_placeholder')}
          autoComplete="new-password"
          className="input font-mono text-xs"
          value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)}
          disabled={busy}
          minLength={8}
          required
        />
        <PasswordStrength password={newPwd} />
        <HibpCheck password={newPwd} />
      </div>
      <input
        type="password"
        placeholder={t('security_section.confirm_password_placeholder')}
        autoComplete="new-password"
        className="input font-mono text-xs"
        value={confirmPwd}
        onChange={(e) => setConfirmPwd(e.target.value)}
        disabled={busy}
        required
      />
      {error && (
        <div
          className="text-xs rounded px-3 py-2"
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
          }}
        >
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          className="btn-primary text-xs"
          disabled={busy || !currentPwd || !newPwd || !confirmPwd}
        >
          {busy ? t('security_section.changing') : t('settings.change_password')}
        </button>
        <button
          type="button"
          className="btn-outline text-xs"
          onClick={onClose}
          disabled={busy}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}
